import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram, FileText } from 'lucide-react';
import { GmedLogo } from './Logo';
import { openCookieSettings } from './ui/cookie-banner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [footerData, setFooterData] = useState(null);

  useEffect(() => {
    loadFooterData();
  }, []);

  const loadFooterData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/footer`);
      setFooterData(response.data);
    } catch (error) {
      // Use defaults if API fails
      setFooterData(null);
    }
  };

  // Default values
  const defaults = {
    company_name: 'Gmed Srl',
    tagline_it: 'Innovazione e qualità nel settore medicale',
    tagline_en: 'Innovation and quality in the medical sector',
    address_line1: 'Via Selvamaggio, 19',
    address_line2: '53034 - Colle di Val d\'Elsa (SI)',
    email: 'comunicazioni@gmed.it',
    partita_iva: '01203470529',
    codice_fiscale: '01203470529',
    codice_sdi: 'KRRH6B9',
  };

  const data = { ...defaults, ...(footerData || {}) };
  const tagline = i18n.language === 'en' ? (data.tagline_en || data.tagline_it) : data.tagline_it;

  // Parse custom fields
  let customFields = [];
  if (data.custom_fields) {
    try {
      customFields = JSON.parse(data.custom_fields);
    } catch (e) {
      customFields = [];
    }
  }

  // Legal info shown in the footer bottom row, only when actually filled in from admin
  const hasValue = (v) => typeof v === 'string' ? v.trim().length > 0 : !!v;
  const legalItems = [
    hasValue(data.partita_iva) && `P.IVA: ${data.partita_iva.trim()}`,
    hasValue(data.codice_fiscale) && data.codice_fiscale.trim() !== (data.partita_iva || '').trim() && `C.F.: ${data.codice_fiscale.trim()}`,
    hasValue(data.codice_sdi) && `SDI: ${data.codice_sdi.trim()}`,
    hasValue(data.rea) && `REA: ${data.rea.trim()}`,
    hasValue(data.capitale_sociale) && `Cap. Soc.: ${data.capitale_sociale.trim()}${/i\.?\s?v\.?$/i.test(data.capitale_sociale.trim()) ? '' : ' i.v.'}`,
    hasValue(data.pec) && `PEC: ${data.pec.trim()}`,
    ...customFields.map((field) => hasValue(field?.label) && hasValue(field?.value) && `${field.label.trim()}: ${field.value.trim()}`),
  ].filter(Boolean);

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/products', label: t('nav.products') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const services = [
    { path: '/products', label: t('nav.products') },
    { path: '/portfolio', label: t('nav.portfolio') },
    { path: '/certifications', label: t('nav.certifications') },
    { path: '/team', label: t('nav.team') },
  ];

  // Check if social links exist
  const hasSocials = data.facebook_url || data.twitter_url || data.linkedin_url || data.instagram_url;

  return (
    <footer className="relative bg-gradient-dark text-foreground dark:text-white mt-20">
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-primary"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <GmedLogo className="h-10 w-auto" />
            <p className="text-sm text-muted-foreground dark:text-gray-300">
              {tagline || t('footer.description')}
            </p>
            {hasSocials && (
              <div className="flex space-x-3">
                {data.facebook_url && (
                  <a href={data.facebook_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full glass hover:bg-primary hover:text-white transition-all">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {data.twitter_url && (
                  <a href={data.twitter_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full glass hover:bg-primary hover:text-white transition-all">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {data.linkedin_url && (
                  <a href={data.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full glass hover:bg-primary hover:text-white transition-all">
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {data.instagram_url && (
                  <a href={data.instagram_url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full glass hover:bg-primary hover:text-white transition-all">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-4">{t('footer.services')}</h3>
            <ul className="space-y-2">
              {services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info - Dynamic */}
          <div>
            <h3 className="font-display font-semibold text-lg mb-4">{t('nav.contact')}</h3>
            <ul className="space-y-3">
              {/* Address */}
              {(data.address_line1 || data.address_line2) && (
                <li className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground dark:text-gray-300">
                    {data.address_line1}{data.address_line1 && data.address_line2 && ' | '}{data.address_line2}
                  </span>
                </li>
              )}
              
              {/* Phone */}
              {hasValue(data.phone) && (
                <li className="flex items-center space-x-3" data-testid="footer-phone">
                  <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                  <a href={`tel:${data.phone.replace(/\s/g, '')}`} className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors">
                    Tel: {data.phone}
                  </a>
                </li>
              )}
              
              {/* Fax */}
              {hasValue(data.fax) && (
                <li className="flex items-center space-x-3" data-testid="footer-fax">
                  <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-sm text-muted-foreground dark:text-gray-300">
                    Fax: {data.fax}
                  </span>
                </li>
              )}
              
              {/* Email */}
              {hasValue(data.email) && (
                <li className="flex items-center space-x-3" data-testid="footer-email">
                  <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                  <a href={`mailto:${data.email}`} className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors">
                    {data.email}
                  </a>
                </li>
              )}
              
              {/* PEC */}
              {hasValue(data.pec) && (
                <li className="flex items-center space-x-3" data-testid="footer-pec">
                  <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                  <a href={`mailto:${data.pec}`} className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors">
                    PEC: {data.pec}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Legal Info Row */}
        {legalItems.length > 0 && (
          <div className="mt-8 pt-6 border-t border-border/50">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground dark:text-gray-400" data-testid="footer-legal-info">
              {legalItems.map((item, index) => (
                <React.Fragment key={item}>
                  {index > 0 && <span aria-hidden="true" className="text-border">|</span>}
                  <span>{item}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Bottom Bar */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-muted-foreground dark:text-gray-300">
              © {currentYear} {data.company_name || 'Gmed Srl'}. {t('footer.rights')}
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <Link to="/compliance/privacy" className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/compliance/cookie-policy" className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors">
                Cookie Policy
              </Link>
              <button
                type="button"
                onClick={openCookieSettings}
                className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors"
                data-testid="footer-manage-cookies"
              >
                Gestisci cookie
              </button>
              <Link to="/contact?tab=dpo" className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors">
                DPO
              </Link>
              <Link to="/condizioni-vendita" className="text-sm text-muted-foreground dark:text-gray-300 hover:text-primary transition-colors">
                Condizioni di Vendita
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
